import { createContext, ReactNode, useContext, useMemo, useReducer } from 'react'
import { UIElement } from '../types/UIElements'

export type FilterElement = UIElement.GEOFILTER | UIElement.TAGFILTER | UIElement.KEYWORDFILTER
export type SheetElement = UIElement.GALLERY | UIElement.ABOUT
export type LegalPageElement = UIElement.LEGALNOTICE | UIElement.TERMSOFUSE
export const isFilterElement = (element: UIElement): element is FilterElement =>
  element === UIElement.GEOFILTER ||
  element === UIElement.TAGFILTER ||
  element === UIElement.KEYWORDFILTER

export type RoutableElement =
  | FilterElement
  | SheetElement
  | UIElement.EXPLANATION
  | UIElement.LEGALNOTICE

// Only one of these can be open at a time
type MainPanel =
  | { kind: 'welcome' }
  | { kind: 'controls' }
  // nothing, not even the controls: closing the side menu leaves the app like this
  | { kind: 'empty' }
  | { kind: 'sideMenu' }
  | { kind: 'filter'; filter: FilterElement }
  | { kind: 'qrModal' }
  | { kind: 'sheet'; sheet: SheetElement }

// The explanation and a legal page (the legal notice or the terms of use) open on top of the main panel
export interface PanelState {
  main: MainPanel
  explanation: boolean
  legalPage: LegalPageElement | null
}

type PanelAction =
  | { type: 'closeAll' }
  | { type: 'closePanel'; element: UIElement }
  | { type: 'openFilter'; filter: FilterElement }
  | { type: 'openQrModal' }
  | { type: 'openFromRoute'; element: RoutableElement }
  | { type: 'toggleSideMenu' }
  | { type: 'toggleExplanation' }
  | { type: 'toggleExplanationFromMenu' }
  | { type: 'openSheet'; sheet: SheetElement }
  | { type: 'openLegalPage'; page: LegalPageElement }

interface State {
  panels: PanelState
  // picking a filter hides the overlay but keeps the filter open, until the panels change again
  overlayHidden: boolean
}

const empty: MainPanel = { kind: 'empty' }

const only = (main: MainPanel): PanelState => ({ main, explanation: false, legalPage: null })

const isOpenIn = ({ main, explanation, legalPage }: PanelState, element: UIElement): boolean => {
  switch (element) {
    case UIElement.WELCOME:
      return main.kind === 'welcome'
    case UIElement.CONTROLS:
      return main.kind === 'controls'
    case UIElement.SIDEMENU:
      return main.kind === 'sideMenu'
    case UIElement.QRMODAL:
      return main.kind === 'qrModal'
    case UIElement.TAGFILTER:
    case UIElement.KEYWORDFILTER:
    case UIElement.GEOFILTER:
      return main.kind === 'filter' && main.filter === element
    case UIElement.GALLERY:
    case UIElement.ABOUT:
      return main.kind === 'sheet' && main.sheet === element
    case UIElement.EXPLANATION:
      return explanation
    case UIElement.LEGALNOTICE:
    case UIElement.TERMSOFUSE:
      return legalPage === element
  }
}

// the sheets take up the whole screen, so the explanation and the legal pages replace them
const withoutCoveredSheet = (panels: PanelState): PanelState =>
  panels.main.kind === 'sheet' && (panels.explanation || panels.legalPage)
    ? { ...panels, main: empty }
    : panels

const closePanel = (panels: PanelState, element: UIElement): PanelState => {
  if (!isOpenIn(panels, element)) return panels
  if (element === UIElement.EXPLANATION) return { ...panels, explanation: false }
  if (element === UIElement.LEGALNOTICE || element === UIElement.TERMSOFUSE) {
    return { ...panels, legalPage: null }
  }
  return { ...panels, main: empty }
}

const openFromRoute = (element: RoutableElement): PanelState => {
  switch (element) {
    case UIElement.EXPLANATION:
      return { main: empty, explanation: true, legalPage: null }
    case UIElement.LEGALNOTICE:
      return { main: empty, explanation: false, legalPage: element }
    case UIElement.GALLERY:
    case UIElement.ABOUT:
      return only({ kind: 'sheet', sheet: element })
    default:
      return only({ kind: 'filter', filter: element })
  }
}

const panelsReducer = (panels: PanelState, action: PanelAction): PanelState => {
  switch (action.type) {
    // back to the default state with the controls showing
    case 'closeAll':
      return only({ kind: 'controls' })
    case 'closePanel':
      return closePanel(panels, action.element)
    case 'openFilter':
      return only({ kind: 'filter', filter: action.filter })
    case 'openQrModal':
      return only({ kind: 'qrModal' })
    case 'openFromRoute':
      return openFromRoute(action.element)
    case 'toggleSideMenu':
      return only(panels.main.kind === 'sideMenu' ? empty : { kind: 'sideMenu' })
    // the help button opens the explanation on top of whatever is open
    case 'toggleExplanation':
      return withoutCoveredSheet({ ...panels, explanation: !panels.explanation })
    // the side menu replaces everything with the explanation
    case 'toggleExplanationFromMenu':
      return { main: empty, explanation: !panels.explanation, legalPage: null }
    case 'openSheet':
      return withoutCoveredSheet({ ...panels, main: { kind: 'sheet', sheet: action.sheet } })
    case 'openLegalPage':
      return withoutCoveredSheet({
        ...panels,
        main: panels.main.kind === 'sideMenu' ? empty : panels.main,
        legalPage: action.page,
      })
  }
}

const reducer = (state: State, action: PanelAction | { type: 'hideOverlay' }): State => {
  if (action.type === 'hideOverlay') return { ...state, overlayHidden: true }
  const panels = panelsReducer(state.panels, action)
  return panels === state.panels ? state : { panels, overlayHidden: false }
}

const initialState: State = { panels: only({ kind: 'welcome' }), overlayHidden: false }

interface ContextType {
  // changes whenever any panel opens or closes, for effects that react to that
  panelState: PanelState
  isPanelOpen: (element: UIElement) => boolean
  isNothingOpen: boolean
  isOnlyControlsOpen: boolean
  overlayVisible: boolean
  hideOverlay: () => void
  closeAll: () => void
  closePanel: (element: UIElement) => void
  openFilter: (filter: FilterElement) => void
  openQrModal: () => void
  openFromRoute: (element: RoutableElement) => void
  toggleSideMenu: () => void
  toggleExplanation: () => void
  toggleExplanationFromMenu: () => void
  openSheet: (sheet: SheetElement) => void
  openLegalPage: (page: LegalPageElement) => void
}

interface Props {
  children: ReactNode
}

const Context = createContext({} as ContextType)

// Which of the app's panels, menus and modals are open, and the rules for opening them together
const PanelsContext = ({ children }: Props) => {
  const [{ panels, overlayHidden }, dispatch] = useReducer(reducer, initialState)
  const hasNothingOnTop = !panels.explanation && !panels.legalPage

  const actions = useMemo(
    () => ({
      hideOverlay: () => dispatch({ type: 'hideOverlay' }),
      closeAll: () => dispatch({ type: 'closeAll' }),
      closePanel: (element: UIElement) => dispatch({ type: 'closePanel', element }),
      openFilter: (filter: FilterElement) => dispatch({ type: 'openFilter', filter }),
      openQrModal: () => dispatch({ type: 'openQrModal' }),
      openFromRoute: (element: RoutableElement) => dispatch({ type: 'openFromRoute', element }),
      toggleSideMenu: () => dispatch({ type: 'toggleSideMenu' }),
      toggleExplanation: () => dispatch({ type: 'toggleExplanation' }),
      toggleExplanationFromMenu: () => dispatch({ type: 'toggleExplanationFromMenu' }),
      openSheet: (sheet: SheetElement) => dispatch({ type: 'openSheet', sheet }),
      openLegalPage: (page: LegalPageElement) => dispatch({ type: 'openLegalPage', page }),
    }),
    [],
  )

  return (
    <Context.Provider
      value={{
        panelState: panels,
        isPanelOpen: element => isOpenIn(panels, element),
        isNothingOpen: panels.main.kind === 'empty' && hasNothingOnTop,
        isOnlyControlsOpen: panels.main.kind === 'controls' && hasNothingOnTop,
        overlayVisible: !overlayHidden && (panels.main.kind === 'filter' || panels.explanation),
        ...actions,
      }}
    >
      {children}
    </Context.Provider>
  )
}

export const useContextPanels = () => {
  return useContext(Context)
}

export default PanelsContext
